import React, { useEffect, useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { fetchGroupLeaderboard } from '../../api/groupService';
import { formatRelativeTimestamp } from '../../utils/groupActivityFormat';
import { typography } from '../../design-system/tokens';

const VIEWS = {
  season: 'season',
  week: 'week',
  allTime: 'allTime',
};

function rowForView(row, view) {
  if (view === VIEWS.week) {
    return {
      ...row,
      wins: row.wins7d ?? 0,
      losses: row.losses7d ?? 0,
      locksCompleted: row.locksCompleted7d ?? 0,
      currentStreak: row.currentWeekStreak ?? 0,
    };
  }
  if (view === VIEWS.season) {
    return {
      ...row,
      wins: row.winsSeason ?? 0,
      losses: row.lossesSeason ?? 0,
      locksCompleted: row.locksCompletedSeason ?? 0,
      currentStreak: row.currentSeasonStreak ?? 0,
    };
  }
  return row;
}

function HeroRow({ row, rank }) {
  const wins = row.wins ?? 0;
  const losses = row.losses ?? 0;
  const streak = row.currentStreak ?? 0;
  const locks = row.locksCompleted ?? 0;
  const lastActive = row.lastActivityAt
    ? formatRelativeTimestamp(row.lastActivityAt)
    : null;

  return (
    <li className="flex items-center justify-between gap-3 rounded-xl border border-gray-800/80 bg-black/30 px-3 py-2.5">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className="text-sm font-black text-violet-400 tabular-nums w-6 shrink-0">
          #{rank}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-black truncate">{row.displayName || 'Player'}</p>
          {lastActive && (
            <p className="text-[10px] font-bold text-gray-600 mt-0.5">
              Last active {lastActive}
            </p>
          )}
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-black tabular-nums text-gray-100">
          {wins}-{losses}
          {streak > 0 && (
            <span className="text-amber-400 ml-1.5">
              🔥 {streak}W
            </span>
          )}
        </p>
        {locks > 0 && (
          <p className="text-[10px] font-bold text-gray-500 tabular-nums mt-0.5">
            {locks} lock{locks === 1 ? '' : 's'}
          </p>
        )}
      </div>
    </li>
  );
}

function ViewToggle({ view, onChange }) {
  const options = [
    { id: VIEWS.week, label: 'This week' },
    { id: VIEWS.season, label: 'Season' },
    { id: VIEWS.allTime, label: 'All time' },
  ];
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((opt) => (
        <Button
          key={opt.id}
          type="button"
          variant={view === opt.id ? 'admin' : 'ghost'}
          size="sm"
          onClick={() => onChange(opt.id)}
        >
          {opt.label}
        </Button>
      ))}
    </div>
  );
}

export default function GroupLeaderboard({
  groupId,
  groupName,
  data: dataProp,
  loading: loadingProp,
  error: errorProp,
}) {
  const [data, setData] = useState(dataProp ?? null);
  const [error, setError] = useState(errorProp ?? '');
  const [loading, setLoading] = useState(loadingProp ?? !dataProp);
  const [view, setView] = useState(VIEWS.season);

  useEffect(() => {
    if (dataProp !== undefined) {
      setData(dataProp);
      setLoading(loadingProp ?? false);
      setError(errorProp ?? '');
      return undefined;
    }
    if (!groupId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchGroupLeaderboard(groupId)
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(err?.message || 'Could not load leaderboard');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [groupId, dataProp, loadingProp, errorProp]);

  const sourceRows = view === VIEWS.week
    ? (data?.thisWeek || [])
    : view === VIEWS.season
      ? (data?.thisSeason || [])
      : (data?.allTime || []);

  const displayRows = sourceRows.map((row) => rowForView(row, view));

  const hasAnyActivity = displayRows.some(
    (r) => (r.wins ?? 0) + (r.losses ?? 0) + (r.locksCompleted ?? 0) > 0,
  );

  const seasonLabel = data?.season?.seasonName || 'Season 1';
  const weekCompetitionCopy = view === VIEWS.week
    ? 'Top member this week'
    : null;

  return (
    <Card variant="admin" className="p-4 border-violet-500/25">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <p className={`${typography.microWide} text-violet-300 mb-1`}>
            🏆 {groupName || 'League standings'}
          </p>
          {weekCompetitionCopy && (
            <p className="text-sm font-black text-amber-300/90 mb-1">{weekCompetitionCopy}</p>
          )}
          <p className="text-xs text-gray-500 font-medium">
            {view === VIEWS.week
              ? 'This week · wins, losses & streak — no balances or payouts.'
              : view === VIEWS.season
                ? `${seasonLabel} · wins, losses & streak — no balances or payouts.`
                : 'All-time · wins, losses & streak — no balances or payouts.'}
          </p>
        </div>
        {!loading && !error && (data?.allTime?.length ?? 0) > 0 && (
          <ViewToggle view={view} onChange={setView} />
        )}
      </div>

      {loading && <p className="text-sm text-gray-500 font-bold">Loading standings…</p>}
      {error && <p className="text-sm text-amber-400 font-bold">{error}</p>}

      {!loading && !error && (
        hasAnyActivity ? (
          <ol className="space-y-2">
            {displayRows.map((row, idx) => (
              <HeroRow key={row.userId} row={row} rank={idx + 1} />
            ))}
          </ol>
        ) : (
          <p className="text-sm text-gray-500 font-medium">
            No settled group locks yet for this view. Post a lock to start the standings.
          </p>
        )
      )}
    </Card>
  );
}

export function memberStatsFromLeaderboard(allTimeRows, userId) {
  const row = (allTimeRows || []).find((r) => r.userId === userId);
  if (!row) return null;
  return {
    wins: row.wins ?? 0,
    losses: row.losses ?? 0,
    locksCompleted: row.locksCompleted ?? 0,
    streak: row.currentStreak ?? 0,
    lastActivityAt: row.lastActivityAt ?? null,
  };
}
